import type { FC, MouseEventHandler } from "react";
import React, { useRef, useMemo } from "react";

import cn from "classnames";

import { Overlay } from "../../helpers/overlays/Overlay/Overlay";
import { useActivatableElement } from "../../hooks/useActiveElement";
import { useMouseDownAnimation } from "../../hooks/useMouseDownAnimation";
import { useOverlays } from "../../hooks/useOverlays";
import { extractProps, getChildrenRecord } from "../../utils/react";

import type { SelectionCellProps } from "./types";

import classes from "./SelectionCell.styl";

const ANIMATION_SHOW_DELAY = 500;
const ANIMATION_SHOW_END_DELAY = 250;
const SELECTION_CELL_DISPLAY_NAME = "SelectionCell";

/**
 * Компонент для придания контейнеру свойств включения/выключения опций или для выбора опций из списка, как одиночного,
 * так и множественного. Обязательно должен внутри имееть `<SelectionCell.Control />` и `<SelectionCell.Content />`,
 * их расположение будет соответствовать тому порядку в котором вы их передали в children
 *
 * ## 🚨 Обратите внимание 🚨
 * - Свойство `state` касается только `<SelectionCell />` и не влияет на дочерние компоненты.
 * - При клике по дочерним элементам событие всплывает и срабатывает на `onClick` `<SelectionCell />` компонента. Если нужно отключить такое поведение,
 * разработчик сам должен явно остановить всплытие у дочернего элемента
 *
 * <a href="https://www.figma.com/design/64y1aDfl0lSSpek3sq1Lrt/%F0%9F%8D%AC-Cards-%26-Cells-%26-Views?m=auto&node-id=2253-386736&t=uIHIWpUvKdZt7wWT-1">Спека</a>
 */
export const SelectionCell: FC<SelectionCellProps> = (props) => {
    const { customColorsStyles, currentProps } = useMemo(
        () => extractProps(props, ["customColorsStyles"]),
        [props],
    );
    const {
        state = "enabled",
        align = "center",
        children,
        onClick,
        customVarsStyles,
        view = "primary",
        "data-tsid": tsid,
    } = currentProps;

    const { isElementActive, onActivatableElementMouseDown } =
        useActivatableElement();
    const { overlays, addOverlay, createRemoveOverlayHandler } = useOverlays();

    const selectionCellRef = useRef<HTMLDivElement>(null);
    const selectionCellReactLeft = useRef<number>(0);
    const selectionCellReactTop = useRef<number>(0);
    const { mousedownHandler } = useMouseDownAnimation();
    const isDisableEvents = state !== "enabled";

    const childrenRes = useMemo(
        () =>
            getChildrenRecord(children, "SelectionCell", {
                Content: {
                    optionalRequired: !children,
                },
                Control: {
                    optionalRequired: !children,
                },
                Unknown: {
                    skipElement: true,
                },
            }),
        [children],
    );

    const mouseEnterHandler: MouseEventHandler<HTMLDivElement> = () => {
        const { left, top } = selectionCellRef.current!.getBoundingClientRect();
        selectionCellReactLeft.current = left;
        selectionCellReactTop.current = top;
    };

    const mouseDownHandler: MouseEventHandler<HTMLDivElement> = (e) => {
        mousedownHandler();

        if (!selectionCellRef.current) {
            return;
        }
        addOverlay(selectionCellRef.current, e);
        onActivatableElementMouseDown(e);
    };

    const eventsProps = !isDisableEvents && {
        onMouseDown: mouseDownHandler,
        onMouseEnter: mouseEnterHandler,
        onClick,
    };

    const selectionCellClasses = cn(
        classes["selection-cell"],
        classes[`__aligned-${align}`],
        classes[`__view-${view}`],
        isDisableEvents && classes[`__state-${state}`],
        customColorsStyles,
        customVarsStyles,
    );

    return (
        /** Игнорируем линтер в данном случае, div хоть и имеет onClick мы не хотим делать его интерактивным,
         * за это будет отвечать элемент в Control который будет доступен с клавиатуры */
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events
        <div
            className={selectionCellClasses}
            data-tsid={tsid}
            data-uikit={SELECTION_CELL_DISPLAY_NAME}
            ref={selectionCellRef}
            {...eventsProps}
        >
            <div className={classes["content"]}>
                {Object.values(childrenRes)}

                {Boolean(overlays.length) && (
                    <div className={classes["overlays-container"]}>
                        {overlays.map(({ key, ...props }) => (
                            <Overlay
                                key={key}
                                {...props}
                                removeOverlay={createRemoveOverlayHandler(key)}
                                finishAnimation={!isElementActive}
                                animationShowDelay={ANIMATION_SHOW_DELAY}
                                animationShowEndDelay={ANIMATION_SHOW_END_DELAY}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

SelectionCell.displayName = SELECTION_CELL_DISPLAY_NAME;

export default SelectionCell;
